/**
 * Classic ops: prefer overnight at the away field instead of a useless
 * empty out-and-back (A→home→A) when the next revenue already starts at A.
 * Grok may see the resulting board — it must not invent the sandwich.
 */
import { stampCancel } from "./cancelReason";
import { icaoKey } from "./curfew";
import { isEmptyFerry } from "./ledger";
import type { CharterRequest, Mission } from "./types";

/** Land tonight / depart tomorrow-morning stays inside this window. */
export const OVERNIGHT_SANDWICH_MAX_H = 36;

export type OvernightNextRevenue = {
  originIcao: string;
  destIcao: string;
  departureIso: string;
  requestId?: string;
  missionId?: string;
};

export type OvernightDecision = {
  icao: string;
  homeIcao: string;
  aircraftId: string;
  missionId: string;
  crewId?: string;
  nextOriginIcao: string;
  nextDestIcao: string;
  cancelledFerryIds: string[];
  cancelledRoutes: string[];
  note: string;
  brief: string;
  key: string;
};

export type PendingOvernightCall = {
  id: string;
  key: string;
  icao: string;
  homeIcao: string;
  crewId?: string;
  missionId: string;
  nextRoute: string;
  cancelledRoutes: string[];
  brief: string;
  createdAtUtc: string;
};

function sameIcao(a: string | null | undefined, b: string | null | undefined): boolean {
  const left = icaoKey(a);
  const right = icaoKey(b);
  return Boolean(left) && left === right;
}

function asMs(iso: string | undefined): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  return Number.isFinite(t) ? t : null;
}

function missionArrivalMs(m: Pick<Mission, "departureIso" | "eteMin">): number | null {
  const start = asMs(m.departureIso);
  if (start == null) return null;
  return start + Math.max(0, m.eteMin) * 60_000;
}

export function isOpenEmptyFerry(
  m: Pick<Mission, "flightType" | "status" | "isMaintenance">,
): boolean {
  return isEmptyFerry(m) && (m.status === "pending" || m.status === "in-progress");
}

export function isOppositeFerry(
  a: Pick<Mission, "originIcao" | "destIcao">,
  b: Pick<Mission, "originIcao" | "destIcao">,
): boolean {
  return sameIcao(a.originIcao, b.destIcao) && sameIcao(a.destIcao, b.originIcao);
}

export function overnightNote(icao: string, homeIcao: string, next?: Pick<OvernightNextRevenue, "originIcao" | "destIcao">): string {
  const field = icaoKey(icao);
  const home = icaoKey(homeIcao) || "home";
  const nextBit = next ? ` Next revenue ${icaoKey(next.originIcao)}–${icaoKey(next.destIcao)} from the field.` : "";
  return `OVERNIGHT  stay at ${field} — cancelled empty ${home} out-and-back.${nextBit}`;
}

export function composeOvernightCallBrief(input: {
  icao: string;
  homeIcao: string;
  nextOriginIcao: string;
  nextDestIcao: string;
  cancelledRoutes?: string[];
}): string {
  const field = icaoKey(input.icao);
  const home = icaoKey(input.homeIcao);
  const next = `${icaoKey(input.nextOriginIcao)}–${icaoKey(input.nextDestIcao)}`;
  const cancelled = input.cancelledRoutes?.length ? ` Empty ${input.cancelledRoutes.join(" and ")} cancelled.` : "";
  return [
    "Celtic Air Dispatch.",
    `Schedule change: overnight at ${field} instead of ferrying home to ${home}.`,
    `Morning departure stays ${next}.${cancelled}`,
    "No inbox message — this is the call.",
  ].join(" ");
}

export function overnightCallKey(input: { aircraftId: string; icao: string; missionId: string }): string {
  return `${input.aircraftId}:${icaoKey(input.icao)}:${input.missionId}`;
}

function isOpenRevenue(
  row: Pick<CharterRequest, "status" | "flightType" | "mxPositioning">,
): boolean {
  if (row.flightType === "ferry") return false;
  if (row.mxPositioning) return false;
  return row.status === "new" || row.status === "assigned" || row.status === "accepted";
}

function isOpenRevenueMission(m: Pick<Mission, "status" | "flightType" | "isMaintenance">): boolean {
  if (m.status === "cancelled" || m.status === "completed") return false;
  if (m.flightType === "ferry" || m.isMaintenance) return false;
  return true;
}

/** Next paying sector that originates at `icao` soon after `afterUtc`. */
export function nextRevenueFromField(input: {
  icao: string;
  afterUtc: number;
  aircraftId?: string;
  missions: ReadonlyArray<
    Pick<Mission, "id" | "aircraftId" | "originIcao" | "destIcao" | "departureIso" | "status" | "flightType" | "isMaintenance" | "inboxRequestId">
  >;
  inbox: ReadonlyArray<
    Pick<
      CharterRequest,
      "id" | "status" | "originIcao" | "destIcao" | "departureIso" | "flightType" | "mxPositioning" | "suggestedAircraftId"
    >
  >;
  excludeRequestId?: string;
  excludeMissionId?: string;
  windowH?: number;
}): OvernightNextRevenue | null {
  const field = icaoKey(input.icao);
  if (!field) return null;
  const windowMs = (input.windowH ?? OVERNIGHT_SANDWICH_MAX_H) * 3600_000;
  const latest = input.afterUtc + windowMs;
  const hits: OvernightNextRevenue[] = [];

  for (const m of input.missions) {
    if (m.id === input.excludeMissionId) continue;
    if (!isOpenRevenueMission(m)) continue;
    if (input.aircraftId && m.aircraftId && m.aircraftId !== input.aircraftId) continue;
    if (!sameIcao(m.originIcao, field)) continue;
    const dep = asMs(m.departureIso);
    if (dep == null || dep < input.afterUtc - 60_000 || dep > latest) continue;
    hits.push({
      originIcao: icaoKey(m.originIcao),
      destIcao: icaoKey(m.destIcao),
      departureIso: m.departureIso,
      requestId: m.inboxRequestId,
      missionId: m.id,
    });
  }

  for (const row of input.inbox) {
    if (row.id === input.excludeRequestId) continue;
    if (!isOpenRevenue(row)) continue;
    if (input.aircraftId && row.suggestedAircraftId && row.suggestedAircraftId !== input.aircraftId) continue;
    if (!sameIcao(row.originIcao, field)) continue;
    const dep = asMs(row.departureIso);
    if (dep == null || dep < input.afterUtc - 60_000 || dep > latest) continue;
    if (hits.some((h) => h.requestId === row.id || h.missionId && row.id === h.requestId)) continue;
    hits.push({
      originIcao: icaoKey(row.originIcao),
      destIcao: icaoKey(row.destIcao),
      departureIso: row.departureIso,
      requestId: row.id,
    });
  }

  hits.sort((a, b) => (asMs(a.departureIso) ?? 0) - (asMs(b.departureIso) ?? 0));
  return hits[0] ?? null;
}

export function shouldSkipReturnFerry(input: {
  destIcao: string;
  homeIcao: string;
  arrivalUtc: number;
  aircraftId?: string;
  missions: ReadonlyArray<Mission>;
  inbox: ReadonlyArray<CharterRequest>;
  excludeRequestId?: string;
  excludeMissionId?: string;
}): OvernightNextRevenue | null {
  if (sameIcao(input.destIcao, input.homeIcao)) return null;
  return nextRevenueFromField({
    icao: input.destIcao,
    afterUtc: input.arrivalUtc,
    aircraftId: input.aircraftId,
    missions: input.missions,
    inbox: input.inbox,
    excludeRequestId: input.excludeRequestId,
    excludeMissionId: input.excludeMissionId,
  });
}

/** Pending empty A→home or home→A on this tail (the sandwich pair). */
export function sandwichFerryIds(input: {
  awayIcao: string;
  homeIcao: string;
  aircraftId?: string;
  missions: ReadonlyArray<Mission>;
  extra?: ReadonlyArray<Mission | null | undefined>;
}): string[] {
  const away = icaoKey(input.awayIcao);
  const home = icaoKey(input.homeIcao);
  if (!away || !home || away === home) return [];
  const rows = [...input.missions, ...(input.extra ?? [])].filter((m): m is Mission => Boolean(m));
  const ids = new Set<string>();
  for (const m of rows) {
    if (!isOpenEmptyFerry(m)) continue;
    if (input.aircraftId && m.aircraftId && m.aircraftId !== input.aircraftId) continue;
    const outAndBack =
      (sameIcao(m.originIcao, away) && sameIcao(m.destIcao, home)) ||
      (sameIcao(m.originIcao, home) && sameIcao(m.destIcao, away));
    if (outAndBack) ids.add(m.id);
  }
  return [...ids];
}

export function buildOvernightDecision(input: {
  icao: string;
  homeIcao: string;
  aircraftId: string;
  missionId: string;
  crewId?: string;
  next: OvernightNextRevenue;
  cancelled: ReadonlyArray<Pick<Mission, "id" | "originIcao" | "destIcao">>;
}): OvernightDecision {
  const cancelledRoutes = input.cancelled.map((m) => `${icaoKey(m.originIcao)}–${icaoKey(m.destIcao)}`);
  const note = overnightNote(input.icao, input.homeIcao, input.next);
  return {
    icao: icaoKey(input.icao),
    homeIcao: icaoKey(input.homeIcao),
    aircraftId: input.aircraftId,
    missionId: input.missionId,
    crewId: input.crewId,
    nextOriginIcao: icaoKey(input.next.originIcao),
    nextDestIcao: icaoKey(input.next.destIcao),
    cancelledFerryIds: input.cancelled.map((m) => m.id),
    cancelledRoutes,
    note,
    brief: composeOvernightCallBrief({
      icao: input.icao,
      homeIcao: input.homeIcao,
      nextOriginIcao: input.next.originIcao,
      nextDestIcao: input.next.destIcao,
      cancelledRoutes,
    }),
    key: overnightCallKey({ aircraftId: input.aircraftId, icao: input.icao, missionId: input.missionId }),
  };
}

export function stampOvernightOnMission(mission: Mission, decision: OvernightDecision): Mission {
  const warning = decision.note;
  return {
    ...mission,
    overnightAtIcao: decision.icao,
    weatherNote: mission.weatherNote?.includes("Overnight")
      ? mission.weatherNote
      : `Overnight at ${decision.icao} — empty ${decision.homeIcao} ferry cancelled.`,
    dispatchWarning: mission.dispatchWarning?.includes("OVERNIGHT") ? mission.dispatchWarning : warning,
    purpose:
      mission.flightType === "ferry"
        ? mission.purpose
        : /overnight/i.test(mission.purpose)
          ? mission.purpose
          : `${mission.purpose} · overnight ${decision.icao}`,
    overnightNotifiedAt: mission.overnightNotifiedAt,
  };
}

export function cancelFerryForOvernight(mission: Mission, decision: OvernightDecision): Mission {
  const route = `${icaoKey(mission.originIcao)}–${icaoKey(mission.destIcao)}`;
  return {
    ...stampCancel(mission, {
      cancelReason: "ferry_sandwich",
      cancelReasonNote: `Overnight at ${decision.icao} instead of empty ${route}`,
    }),
    dispatchWarning: `Cancelled — overnight at ${decision.icao} instead of empty ${route}.`,
    weatherNote: `Overnight at ${decision.icao} — ferry removed.`,
    overnightAtIcao: decision.icao,
  };
}

export function applyOvernightToMissions(
  missions: Mission[],
  decision: OvernightDecision | null,
  extraKeepIds: ReadonlySet<string> = new Set(),
): Mission[] {
  if (!decision) return missions;
  const cancel = new Set(decision.cancelledFerryIds);
  return missions.map((m) => {
    if (cancel.has(m.id) && !extraKeepIds.has(m.id)) {
      return cancelFerryForOvernight(m, decision);
    }
    if (m.id === decision.missionId || m.overnightAtIcao === decision.icao) {
      return stampOvernightOnMission(m, decision);
    }
    if (sameIcao(m.originIcao, decision.icao) && isOpenRevenueMission(m)) {
      return stampOvernightOnMission(m, decision);
    }
    if (sameIcao(m.destIcao, decision.icao) && isOpenRevenueMission(m)) {
      return stampOvernightOnMission(m, decision);
    }
    return m;
  });
}

export function alreadyNotifiedOvernight(
  missions: ReadonlyArray<Pick<Mission, "overnightAtIcao" | "overnightNotifiedAt" | "aircraftId">>,
  decision: Pick<OvernightDecision, "icao" | "aircraftId">,
): boolean {
  return missions.some(
    (m) =>
      sameIcao(m.overnightAtIcao, decision.icao) &&
      Boolean(m.overnightNotifiedAt) &&
      (!m.aircraftId || !decision.aircraftId || m.aircraftId === decision.aircraftId),
  );
}

export function pendingCallFromDecision(
  decision: OvernightDecision,
  now = new Date(),
): PendingOvernightCall {
  return {
    id: `ovn-${now.getTime().toString(36)}`,
    key: decision.key,
    icao: decision.icao,
    homeIcao: decision.homeIcao,
    crewId: decision.crewId,
    missionId: decision.missionId,
    nextRoute: `${decision.nextOriginIcao}–${decision.nextDestIcao}`,
    cancelledRoutes: decision.cancelledRoutes,
    brief: decision.brief,
    createdAtUtc: now.toISOString(),
  };
}

export function markOvernightNotified(missions: Mission[], icao: string, now = new Date()): Mission[] {
  const field = icaoKey(icao);
  const iso = now.toISOString();
  return missions.map((m) =>
    sameIcao(m.overnightAtIcao, field) && !m.overnightNotifiedAt ? { ...m, overnightNotifiedAt: iso } : m,
  );
}

/**
 * Assign-time planner: skip / cancel the A→home→A ferry pair when the next
 * revenue already originates at A.
 */
export function planOvernightVsFerries(input: {
  request: Pick<CharterRequest, "id" | "originIcao" | "destIcao" | "departureIso">;
  charter: Pick<Mission, "id" | "aircraftId" | "originIcao" | "destIcao" | "departureIso" | "eteMin">;
  homeIcao: string;
  crewId?: string;
  inbound?: Mission | null;
  returnFerry?: Mission | null;
  missions: Mission[];
  inbox: CharterRequest[];
}): {
  skipInbound: boolean;
  skipReturn: boolean;
  cancelMissionIds: string[];
  overnight: OvernightDecision | null;
} {
  const home = icaoKey(input.homeIcao);
  const origin = icaoKey(input.request.originIcao);
  const dest = icaoKey(input.request.destIcao);
  const arrival = missionArrivalMs(input.charter) ?? asMs(input.request.departureIso) ?? Date.now();
  const extra = [input.inbound, input.returnFerry];

  const thisAsNext: OvernightNextRevenue = {
    originIcao: origin,
    destIcao: dest,
    departureIso: input.request.departureIso,
    requestId: input.request.id,
    missionId: input.charter.id,
  };
  const nextFromOrigin = nextRevenueFromField({
    icao: origin,
    afterUtc: asMs(input.request.departureIso) ?? arrival,
    aircraftId: input.charter.aircraftId,
    missions: input.missions,
    inbox: input.inbox,
    excludeRequestId: input.request.id,
    excludeMissionId: input.charter.id,
  });
  const nextFromDest = shouldSkipReturnFerry({
    destIcao: dest,
    homeIcao: home,
    arrivalUtc: arrival,
    aircraftId: input.charter.aircraftId,
    missions: input.missions,
    inbox: input.inbox,
    excludeRequestId: input.request.id,
    excludeMissionId: input.charter.id,
  });

  // Assigning the next sector from the away field: cancel home↔away pair.
  const sandwichFromThisOrigin = !sameIcao(origin, home)
    ? sandwichFerryIds({
        awayIcao: origin,
        homeIcao: home,
        aircraftId: input.charter.aircraftId,
        missions: input.missions,
        extra,
      })
    : [];

  const alreadyOvernightHere = input.missions.some((m) => sameIcao(m.overnightAtIcao, origin));
  const stayAtOrigin = Boolean(
    (nextFromOrigin || sandwichFromThisOrigin.length || alreadyOvernightHere) && !sameIcao(origin, home),
  );
  const stayAtDest = Boolean(nextFromDest && !sameIcao(dest, home));
  const away = stayAtOrigin ? origin : stayAtDest ? dest : "";
  const next = stayAtOrigin ? nextFromOrigin ?? thisAsNext : nextFromDest;

  const sandwichFromThisDest = stayAtDest
    ? sandwichFerryIds({
        awayIcao: dest,
        homeIcao: home,
        aircraftId: input.charter.aircraftId,
        missions: input.missions,
        extra,
      })
    : [];

  const skipInbound = stayAtOrigin || sandwichFromThisOrigin.length > 0;
  const skipReturn = stayAtDest || stayAtOrigin;
  const cancelMissionIds = [...new Set([...sandwichFromThisOrigin, ...sandwichFromThisDest])];

  if (!away || !next) {
    return { skipInbound, skipReturn, cancelMissionIds, overnight: null };
  }

  const cancelled = [...input.missions, ...extra].filter(
    (m): m is Mission => Boolean(m && cancelMissionIds.includes(m.id)),
  );
  if (input.inbound && skipInbound && !cancelled.some((m) => m.id === input.inbound?.id)) {
    cancelled.push(input.inbound);
    cancelMissionIds.push(input.inbound.id);
  }
  if (input.returnFerry && skipReturn && !cancelled.some((m) => m.id === input.returnFerry?.id)) {
    cancelled.push(input.returnFerry);
    cancelMissionIds.push(input.returnFerry.id);
  }

  const overnight = buildOvernightDecision({
    icao: away,
    homeIcao: home,
    aircraftId: input.charter.aircraftId,
    missionId: input.charter.id,
    crewId: input.crewId,
    next,
    cancelled,
  });

  return { skipInbound, skipReturn, cancelMissionIds: [...new Set(cancelMissionIds)], overnight };
}

/** Repair already-filed A→B + B→A empty pair when next revenue starts at A. */
export function collapseExistingSandwich(input: {
  missions: Mission[];
  inbox: CharterRequest[];
  homeIcao: string;
  aircraftId?: string;
  crewId?: string;
}): { missions: Mission[]; overnight: OvernightDecision | null; cancelMissionIds: string[] } {
  const home = icaoKey(input.homeIcao);
  const ferries = input.missions.filter((m) => isOpenEmptyFerry(m) && (!input.aircraftId || m.aircraftId === input.aircraftId));
  for (const outbound of ferries) {
    const mate = ferries.find((m) => m.id !== outbound.id && isOppositeFerry(outbound, m));
    if (!mate) continue;
    const away = sameIcao(outbound.destIcao, home) ? outbound.originIcao : outbound.destIcao;
    if (sameIcao(away, home)) continue;
    const after = asMs(outbound.departureIso) ?? Date.now();
    const next = nextRevenueFromField({
      icao: away,
      afterUtc: after,
      aircraftId: outbound.aircraftId,
      missions: input.missions,
      inbox: input.inbox,
    });
    if (!next) continue;
    const cancelled = [outbound, mate];
    const parent =
      input.missions.find((m) => m.id === outbound.parentMissionId && isOpenRevenueMission(m)) ??
      input.missions.find((m) => sameIcao(m.originIcao, away) && isOpenRevenueMission(m)) ??
      input.missions.find((m) => sameIcao(m.destIcao, away) && isOpenRevenueMission(m));
    const overnight = buildOvernightDecision({
      icao: away,
      homeIcao: home,
      aircraftId: outbound.aircraftId,
      missionId: parent?.id ?? next.missionId ?? outbound.parentMissionId ?? outbound.id,
      crewId: input.crewId,
      next,
      cancelled,
    });
    return {
      missions: applyOvernightToMissions(input.missions, overnight),
      overnight,
      cancelMissionIds: cancelled.map((m) => m.id),
    };
  }
  return { missions: input.missions, overnight: null, cancelMissionIds: [] };
}
